import {Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {animate, AnimationBuilder, AnimationPlayer, style} from '@angular/animations';
import {Subject} from 'rxjs';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';
import {filter, map, takeUntil} from 'rxjs/operators';
import {NavigationEnd, Router, RouterEvent, Scroll} from '@angular/router';
import {FormControl} from '@angular/forms';

import {
    AUDIT_USER_ACTION_OBJECT_ID_DELIMITER,
    AuditUserActivity,
    AuditUserActivityActionFriendlyNames,
    AuditUserActivityActionFriendlyTitles,
    AuditUserActivityActionObjectIdList,
    AuditUserActivityActionObjectIdMap,
    AuditUserActivityActionObjectIdTitleList,
    AuditUserActivityActionRelatedPagesMap,
    AuditUserActivityActions,
    AuditUserActivityPagesList,
    AuditUserActivityParameter,
    AuditUserActivityPrefixlessActions,
    AuditUserActivitySuffixlessActions,
    AuditUserActivityView,
    AuditUserActivityViewLink,
} from '@app/core/model/audit-user-activity.model';
import {
    AuditUserActivityService
} from '@app/core/service/audit-user-activity.service';
import {DirectoryUser, User} from '@app/core/model/user';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {ApplicationProviderListModel} from '@app/views/admin/application-settings/models/application-provider-list.model';
import {ApplicationGalleryModel} from '@app/views/admin/application-settings/models/application-gallery.model';
import {ApplicationType} from '@app/views/admin/application-settings/models/application-type.model';
import {DirectoryOrganization, DistrictProfileModel, SchoolProfileModel} from '@app/core/model/entity-profile';
import {InstitutionsVendorListModel, VendorInstitutionsReportItem} from '@app/core/model/institutional-model';
import {DraftIntegrationReportItem, IntegrationReportItem} from '@app/views/dashboard/dashboard.model';
import {OrgsService} from '@app/core/service/orgs.service';
import {Application} from '@app/views/admin/application-settings/models/application.model';

@Component({
    selector: 'ngc-user-audit',
    templateUrl: './user-audit.component.html',
    styleUrls: ['./user-audit.component.scss']
})
export class UserAuditComponent implements OnInit, OnDestroy {

    @ViewChild('openButton') openButton;
    @ViewChild('panel') panel;
    @ViewChild('overlay') overlay: ElementRef;

    player: AnimationPlayer;
    isLoading: boolean;
    isLoadingNext: boolean;
    isLoadingDetails: boolean;
    orgGuid: string;
    auditUserActivityData: AuditUserActivity[] = [];
    displayedColumns: string[] = ['executedAt', 'actionName', 'userEmail', 'objectId'];
    currentUserActivityItem: AuditUserActivity;
    currentUserActivityDetails: AuditUserActivity;
    user: User;
    isSysAdmin: boolean;
    userActionsLength: number;
    hasMoreAuditItems: boolean;
    barClosing = false;
    userActionsFilterCtrl = new FormControl<(string)[]>([]);
    userActionsList: string[] = [];
    filteredUserActionsList: (string)[] = [];

    private unsubscribeOnClosePanel$ = new Subject<void>();
    private unsubscribeOnDestroy$ = new Subject<void>();

    readonly DEFAULT_LIMIT = 20;
    readonly AuditUserActivityActions = AuditUserActivityActions;
    readonly AuditUserActivityActionObjectIdMap = AuditUserActivityActionObjectIdMap;
    readonly AuditUserActivityActionObjectIdList = AuditUserActivityActionObjectIdList;
    readonly AuditUserActivityActionObjectIdTitleList = AuditUserActivityActionObjectIdTitleList;
    readonly AuditUserActivityActionFriendlyNames = AuditUserActivityActionFriendlyNames;
    readonly AuditUserActivityActionFriendlyTitles = AuditUserActivityActionFriendlyTitles;
    readonly AuditUserActivityPagesList = AuditUserActivityPagesList;
    readonly AuditUserActivityPrefixlessActions = AuditUserActivityPrefixlessActions;
    readonly AuditUserActivitySuffixlessActions = AuditUserActivitySuffixlessActions;
    readonly LOADING_ERROR_MESSAGE = 'Unknown error occurred while loading user activity data.';
    readonly ALL = 'All';
    readonly SYS_ADMIN_USERNAME = 'sysadm';

    @HostBinding('class.bar-closed') barClosed: boolean;

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKeydownHandler(event: KeyboardEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.closeBar();
    }

    constructor(
        public auditUserActivityService: AuditUserActivityService,
        private animationBuilder: AnimationBuilder,
        public snackBar: MatSnackBar,
        public authenticatedUserService: AuthenticatedUserService,
        private router: Router,
    ) {
        this.barClosed = true;
    }

    ngOnInit() {
        this.getUser();

        this.auditUserActivityService.openAuditUserActivityPanel$.pipe(takeUntil(this.unsubscribeOnDestroy$))
            .subscribe((openAuditUserActivityPanel: boolean) => {
                if (openAuditUserActivityPanel) {
                    this.onOpenBar();
                    this.auditUserActivityService.openAuditUserActivityPanel$.next(false);
                }
            });

        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map((event: RouterEvent) => (event instanceof Scroll) ? event.routerEvent : event),
            takeUntil(this.unsubscribeOnDestroy$)
        ).subscribe((event: NavigationEnd) => {
            if (!this.barClosed && !this.barClosing) {
                this.closeBar();
            }
        });
    }

    private onOpenBar() {
        if (this.barClosing) {
            setTimeout(() => {
                this.onOpenBar();
            });
        } else {
            this.openBar();
        }
    }

    private getUser() {
        this.authenticatedUserService.authenticatedUser$.pipe(takeUntil(this.unsubscribeOnClosePanel$))
            .subscribe((user: User) => {
                this.user = user;
                this.isSysAdmin = this.authenticatedUserService.isSysAdmin(user);

                if (this.user && !this.isSysAdmin) {
                    this.auditUserActivityService.orgGuid = this.user.orgGuid;
                }
            });
    }

    closeBar() {
        this.player =
            this.animationBuilder
                .build([
                    style({transform: 'translate3d(0,0,0)'}),
                    animate('400ms ease', style({transform: 'translate3d(100%,0,0)'}))
                ]).create(this.panel.nativeElement);

        this.player.play();
        this.barClosing = true;

        this.player.onDone(() => {
            this.barClosed = true;
            this.barClosing = false;
            this.auditUserActivityData = [];
            this.userActionsLength = 0;
            this.isLoadingDetails = false;
            this.isLoading = false;
            this.isLoadingNext = false;
            this.userActionsList = [];
            this.filteredUserActionsList = [];
            this.userActionsFilterCtrl.setValue([]);
            this.onCloseUserActivityDetails();
            this.onUnsubscribe(this.unsubscribeOnClosePanel$);
        });
    }

    openBar() {
        this.barClosed = false;
        this.orgGuid = this.auditUserActivityService.orgGuid;

        if (this.auditUserActivityService.currentAuditUserActivityPage) {
            this.isLoading = true;
            this.setUserActionsList();

            this.getAuditUserActivityData();
        }

        this.player =
            this.animationBuilder
                .build([
                    style({transform: 'translate3d(100%,0,0)'}),
                    animate('400ms ease', style({transform: 'translate3d(0,0,0)'}))
                ]).create(this.panel.nativeElement);

        this.player.play();
    }

    private setUserActionsList(): void {
        // get actions with unique title
        const userActionsListObj = {};
        this.userActionsList = [];
        this.auditUserActivityService.auditUserActivityActions.forEach((userAction: AuditUserActivityActions) => {
            userActionsListObj[this.AuditUserActivityActionFriendlyTitles[userAction]] = userAction;
        });
        this.userActionsList = Object.values(userActionsListObj);
        this.userActionsList.sort((userAction1: AuditUserActivityActions, userAction2: AuditUserActivityActions) => {
            const userActionTitle1 = this.AuditUserActivityActionFriendlyTitles[userAction1].toLocaleLowerCase();
            const userActionTitle2 = this.AuditUserActivityActionFriendlyTitles[userAction2].toLocaleLowerCase();
            return userActionTitle1.localeCompare(userActionTitle2);
        });

        this.filteredUserActionsList = this.userActionsList.filter((userAction: any) => {
            const result = (userAction != AuditUserActivityActions.AUTH_LOGIN_SUCCESS)
                && (userAction != AuditUserActivityActions.AUTH_LOGIN_FAIL_BAD_USERNAME)
                && (userAction != AuditUserActivityActions.AUTH_LOGIN_FAIL_BAD_CREDENTIAL)
                && (userAction != AuditUserActivityActions.AUTH_LOGIN_FROM_ETX)
                && (userAction != this.ALL);

            return result;
        });

        if (this.filteredUserActionsList.length === this.userActionsList.length) {
            this.filteredUserActionsList.unshift(this.ALL);
        }
        this.userActionsList.unshift(this.ALL);
        this.userActionsFilterCtrl.setValue(this.filteredUserActionsList);
    }

    onScroll() {
        if (!this.barClosed) {
            if (!this.isLoadingNext) {
                this.isLoadingNext = true;
                this.getAuditUserActivityData();
            }
        }
    }

    getAuditUserActivityData() {
        const userActions = this.getFilteredUserActionsList();

        this.auditUserActivityService.getAuditUserActivityData(
            this.DEFAULT_LIMIT,
            this.auditUserActivityData?.length,
            userActions
        ).pipe(takeUntil(this.unsubscribeOnClosePanel$))
            .subscribe((
                    {userActionsLength, auditUserActivityData}: { userActionsLength: number; auditUserActivityData: AuditUserActivity[] }
                ) => {
                    this.isLoading = false;
                    this.isLoadingNext = false;
                    this.hasMoreAuditItems = (auditUserActivityData.length >= this.DEFAULT_LIMIT);

                    auditUserActivityData = auditUserActivityData.map((auditItem: AuditUserActivity, index: number) => {

                        const currentDate = new Date(new Date(auditItem?.executedAt).toDateString()).getDate();
                        const lastAuditItem = (index > 0)
                            ? auditUserActivityData[index - 1]
                            : this.auditUserActivityData[this.auditUserActivityData.length - 1];
                        const dateBeforeCurrent = lastAuditItem
                            ? new Date(new Date(lastAuditItem.executedAt).toDateString()).getDate()
                            : null;

                        auditItem.isFirstListItemInADay = !dateBeforeCurrent
                            || ((dateBeforeCurrent - currentDate) !== 0);
                        this.setAdditionalData(auditItem);

                        return auditItem;
                    });

                    this.auditUserActivityData.push(...auditUserActivityData);
                    this.userActionsLength = userActionsLength;

                    return (auditUserActivityData === null) ? [] : auditUserActivityData;
                },
                (error: any) => {
                    this.snackBar.open(this.LOADING_ERROR_MESSAGE, '', <MatSnackBarConfig>{duration: 3000});
                    this.isLoading = false;
                    this.isLoadingNext = false;
                }
            );
    }

    private getFilteredUserActionsList(): AuditUserActivityActions[] {
        const userActionFriendlyTitlesObj = {};
        this.userActionsFilterCtrl.value.forEach((userAction: AuditUserActivityActions) => {
            userActionFriendlyTitlesObj[AuditUserActivityActionFriendlyTitles[userAction]] = userAction;
        });

        return (<AuditUserActivityActions[]>Object.keys(AuditUserActivityActions))
            .filter((actionName: AuditUserActivityActions) =>
                userActionFriendlyTitlesObj[AuditUserActivityActionFriendlyTitles[actionName]]);
    }

    private setAdditionalData(auditItem: AuditUserActivity): void {

        let orgLink, userInitiatorLink, userObjectIdLink, appLink, objectIdOtherText: AuditUserActivityViewLink;
        let application: Application;
        let appGuid, appName, districtOrgGuid, institutionGuid: string;
        let vendorInstitutionsReportItems: VendorInstitutionsReportItem[];
        let vendorInstitution: InstitutionsVendorListModel;
        let institutionOrg: DirectoryOrganization;
        let integrationReportItems: IntegrationReportItem[];
        let draftIntegrations: DraftIntegrationReportItem[];

        switch (this.auditUserActivityService.currentAuditUserActivityPage) {
            case AuditUserActivityPagesList.ADMINISTRATION_ORGS_TAB_FOR_SYS_ADMIN:

                orgLink = this.getEditOrgAuditViewLink(auditItem, true);
                userInitiatorLink = this.getEditUserInitiatorAuditViewLink(auditItem, true);

                if ((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.USER_GUID)
                    && auditItem?.objectId) {
                    userObjectIdLink = this.getEditUserObjectIdAuditViewLink(auditItem, true);
                }

                auditItem.view = new AuditUserActivityView(
                    orgLink,
                    userInitiatorLink,
                    userObjectIdLink
                );

                break;

            case AuditUserActivityPagesList.ADMINISTRATION_MAIN_ADMINS_TAB:

                orgLink = this.getEditOrgAuditViewLink(auditItem, true);
                userInitiatorLink = this.getEditUserInitiatorAuditViewLink(auditItem, true);

                if (auditItem?.objectId) {
                    userObjectIdLink = this.getEditUserObjectIdAuditViewLink(auditItem, true);
                }

                auditItem.view = new AuditUserActivityView(
                    orgLink,
                    userInitiatorLink,
                    userObjectIdLink
                );

                break;

            case AuditUserActivityPagesList.ADMINISTRATION_EDIT_ORG:

                if (auditItem?.orgGuid !== OrgsService.GG4L_ORG_ID) {
                    orgLink = this.getEditOrgAuditViewLink(auditItem, false);
                }

                userInitiatorLink = this.getEditUserInitiatorAuditViewLink(auditItem, true);

                if ((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.USER_GUID)
                    && auditItem?.objectId) {
                    userObjectIdLink = this.getEditUserObjectIdAuditViewLink(auditItem, true);
                }

                auditItem.view = new AuditUserActivityView(
                    orgLink,
                    userInitiatorLink,
                    userObjectIdLink
                );

                break;

            case AuditUserActivityPagesList.DISTRICT_APP_ADMIN_PRIVATE:

                if (auditItem?.orgGuid !== OrgsService.GG4L_ORG_ID) {
                    orgLink = this.getEditOrgAuditViewLink(auditItem, false);
                }

                userInitiatorLink = this.getEditUserInitiatorAuditViewLink(auditItem, true);

                auditItem.view = new AuditUserActivityView(
                    orgLink,
                    userInitiatorLink
                );

                break;

            case AuditUserActivityPagesList.DISTRICT_APP_ADMIN_PUBLIC:

                if (auditItem?.orgGuid !== OrgsService.GG4L_ORG_ID) {
                    orgLink = this.getEditOrgAuditViewLink(auditItem, false);
                }

                userInitiatorLink = this.getEditUserInitiatorAuditViewLink(auditItem, true);

                auditItem.view = new AuditUserActivityView(
                    orgLink,
                    userInitiatorLink
                );

                break;

            case AuditUserActivityPagesList.APPLICATION_SETTINGS:

                if (auditItem?.orgGuid !== OrgsService.GG4L_ORG_ID) {
                    orgLink = this.getEditOrgAuditViewLink(auditItem, false);
                }

                if (AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.USERNAME) {
                    objectIdOtherText = this.getObjectIdOtherTextAuditViewItem('Username', auditItem?.objectId);
                }

                userInitiatorLink = this.getEditUserInitiatorAuditViewLink(auditItem, true);

                auditItem.view = new AuditUserActivityView(
                    orgLink,
                    userInitiatorLink,
                    null,
                    null,
                    objectIdOtherText
                );

                break;

            case AuditUserActivityPagesList.ADMINISTRATION_EDIT_DISTRICT_ADMIN:

                if (auditItem?.objectId) {

                    if (AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.USER_GUID) {
                        userObjectIdLink = this.getEditUserObjectIdAuditViewLink(auditItem, true);
                    }

                    if (AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.USERNAME) {
                        objectIdOtherText = this.getObjectIdOtherTextAuditViewItem('Username', auditItem?.objectId);
                    }

                    if ((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.INSTITUTION_AND_APPLICATION_GUID)
                        || ((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.APPLICATION_GUID)
                            || (AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.INTEGRATION_ID)
                            || (AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.DNAME))
                        ) {
                        if (AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.INSTITUTION_AND_APPLICATION_GUID) {
                            [institutionGuid, appGuid] = auditItem.objectId.split(AUDIT_USER_ACTION_OBJECT_ID_DELIMITER);
                            auditItem.objectId = appGuid;
                        }
                    }

                    application = this.getProviderApplication(auditItem.objectId);
                    appGuid = application?.guid || auditItem?.objectId || '';
                    appName = application?.name || auditItem?.objectId || '';

                    if (application?.type === ApplicationType.PRIVATE) {
                        appLink = this.getEditAppLinkAuditViewLink(appName, appGuid, auditItem.orgGuid, true);
                    }

                    if (application?.type === ApplicationType.PUBLIC) {
                        districtOrgGuid = this.auditUserActivityService.objectIdValues[AuditUserActivityActionObjectIdList.ORGANIZATION_GUID];
                        institutionOrg = this.auditUserActivityService.objectMaps[AuditUserActivityActionObjectIdList.ORGANIZATION_GUID][districtOrgGuid];
                        appName =
                            (institutionOrg?.orgName && appName)
                                ? `${institutionOrg?.orgName} - ${appName}`
                                : appName || '';
                        appLink = this.getEditAppLinkAuditViewLink(appName, appGuid, districtOrgGuid, false);
                    }

                    if (!appLink) {
                        objectIdOtherText = this.getObjectIdOtherTextAuditViewItem('Application', auditItem?.objectId);
                    }
                }


                if (auditItem?.orgGuid !== OrgsService.GG4L_ORG_ID) {
                    orgLink = this.getEditOrgAuditViewLink(auditItem, false);
                }

                auditItem.view = new AuditUserActivityView(
                    orgLink,
                    null,
                    userObjectIdLink,
                    appLink,
                    objectIdOtherText
                );

                break;

            case AuditUserActivityPagesList.ADMINISTRATION_EDIT_VENDOR_ADMIN:

                if (auditItem?.objectId) {

                    if (AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.USER_GUID) {
                        userObjectIdLink = this.getEditUserObjectIdAuditViewLink(auditItem, true);
                    }

                    if ((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.INSTITUTION_AND_APPLICATION_GUID)
                        && auditItem?.objectId) {
                        [institutionGuid, appGuid] = auditItem.objectId.split(AUDIT_USER_ACTION_OBJECT_ID_DELIMITER);
                        auditItem.objectId = appGuid;

                        institutionOrg = this.auditUserActivityService.objectMaps[AuditUserActivityActionObjectIdList.ORGANIZATION_GUID][institutionGuid];
                        application = this.auditUserActivityService.allVendorsApplications.find((app: ApplicationGalleryModel) =>
                            auditItem.objectId === app.guid
                            || auditItem.objectId === app.integrationId
                            || auditItem.objectId === app.dataSourceRef
                        );

                        appName =
                            (institutionOrg?.orgName && application?.name)
                                ? `${institutionOrg?.orgName} - ${application?.name}`
                                : auditItem?.objectId || '';

                        appLink = this.getDistrictAppAdminLinkAuditViewLink(appName, appGuid, institutionGuid, true);
                    }

                    if (((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.APPLICATION_GUID)
                            || (AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.INTEGRATION_ID)
                            || (AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.DNAME))
                        && auditItem?.objectId) {


                        draftIntegrations = this.auditUserActivityService.draftIntegrations;
                        const currentDraftIntegration = draftIntegrations?.find((draftIntegration: DraftIntegrationReportItem) =>
                            ((draftIntegration.appGuid === auditItem?.objectId) && (draftIntegration.vendorOrgGuid === auditItem.orgGuid))
                            || ((draftIntegration.integrationId === auditItem?.objectId) && (draftIntegration.vendorOrgGuid === auditItem.orgGuid))
                            || ((draftIntegration.notEduToneDname === auditItem?.objectId) && (draftIntegration.vendorOrgGuid === auditItem.orgGuid))
                        );

                        if (currentDraftIntegration?.appGuid) {
                            appGuid = currentDraftIntegration?.appGuid;
                            institutionGuid = currentDraftIntegration?.vendorOrgGuid;
                            application = this.auditUserActivityService.allVendorsApplications.find((app: ApplicationGalleryModel) =>
                                appGuid === app.guid
                                || auditItem.objectId === app.guid
                                || auditItem.objectId === app.integrationId
                                || auditItem.objectId === app.dataSourceRef
                            );
                            institutionOrg = this.auditUserActivityService.objectMaps[AuditUserActivityActionObjectIdList.ORGANIZATION_GUID][institutionGuid];
                            appName = application?.name;
                        }


                        vendorInstitutionsReportItems = this.getVendorInstitutionsReportItems(auditItem.objectId);
                        if ((vendorInstitutionsReportItems?.length === 1)
                            && vendorInstitutionsReportItems[0]?.appGuid
                            && !appName
                            && !institutionGuid
                        ) {
                            appGuid = vendorInstitutionsReportItems[0]?.appGuid;
                            institutionGuid = vendorInstitutionsReportItems[0]?.institutionGuid;
                            application = this.getProviderApplication(appGuid);
                            vendorInstitution = this.auditUserActivityService.objectMaps[AuditUserActivityActionObjectIdList.ORGANIZATION_GUID][vendorInstitutionsReportItems[0]?.institutionGuid];
                            appName =
                                ((<any>vendorInstitution)?.orgName && application.name)
                                    ? `${(<any>vendorInstitution)?.orgName} - ${application?.name}`
                                    : auditItem?.objectId || '';
                        }


                        integrationReportItems = this.auditUserActivityService.integrationReportItems;
                        const currentIntegrationReportItems = integrationReportItems?.filter((integrationReportItem: IntegrationReportItem) =>
                            (integrationReportItem.applicationGuid === auditItem?.objectId)
                            || (integrationReportItem.integrationId === auditItem?.objectId)
                            || (integrationReportItem.notEduToneDname === auditItem?.objectId)
                        );

                        if ((currentIntegrationReportItems?.length === 1)
                            && currentIntegrationReportItems[0]?.applicationGuid
                            && !appName
                            && !institutionGuid
                        ) {
                            appGuid = currentIntegrationReportItems[0]?.applicationGuid;
                            institutionGuid = currentIntegrationReportItems[0]?.organizationGuid;
                            institutionOrg = this.auditUserActivityService.objectMaps[AuditUserActivityActionObjectIdList.ORGANIZATION_GUID][institutionGuid];

                            application = this.auditUserActivityService.allVendorsApplications.find((app: ApplicationGalleryModel) =>
                                auditItem.objectId === app.guid
                                || auditItem.objectId === app.integrationId
                                || auditItem.objectId === app.dataSourceRef
                            );
                            application = this.getProviderApplication(appGuid);

                            appName =
                                (institutionOrg?.orgName && application?.name)
                                    ? `${institutionOrg?.orgName} - ${application?.name}`
                                    : auditItem?.objectId || '';
                        }


                        const relatedPage = AuditUserActivityActionRelatedPagesMap[auditItem.actionName];
                        if (relatedPage === AuditUserActivityPagesList.DISTRICT_APP_ADMIN_PUBLIC) {
                            appLink = this.getDistrictAppAdminLinkAuditViewLink(appName, appGuid, institutionGuid, true);
                        } else {
                            appLink = this.getEditAppLinkAuditViewLink(appName, appGuid, institutionGuid, true, !!currentDraftIntegration);
                        }

                        if (!appLink) {
                            objectIdOtherText = this.getObjectIdOtherTextAuditViewItem('Application', auditItem?.objectId);
                        }
                    }
                }

                if (auditItem?.orgGuid !== OrgsService.GG4L_ORG_ID) {
                    orgLink = this.getEditOrgAuditViewLink(auditItem, false);
                }

                auditItem.view = new AuditUserActivityView(
                    orgLink,
                    null,
                    userObjectIdLink,
                    appLink,
                    objectIdOtherText
                );

                break;


            case AuditUserActivityPagesList.ADMINISTRATION_EDIT_SYS_ADMIN:

                if (auditItem?.orgGuid !== OrgsService.GG4L_ORG_ID) {
                    orgLink = this.getEditOrgAuditViewLink(auditItem, true);
                }

                if ((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.ORGANIZATION_GUID)
                    && auditItem?.objectId) {
                    orgLink = this.getEditOrgAuditViewLink(auditItem, true);
                }

                if ((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.USER_GUID)
                    && auditItem?.objectId) {
                    userObjectIdLink = this.getEditUserObjectIdAuditViewLink(auditItem, true);
                }

                if ((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.USERNAME)
                    && auditItem?.objectId) {
                    userObjectIdLink = this.getEditUserObjectIdAuditViewLink(auditItem, true, auditItem?.objectId);
                }

                if ((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.EMAIL)
                    && auditItem?.objectId) {
                    userObjectIdLink = this.getEditUserObjectIdAuditViewLink(auditItem, true, auditItem?.objectId);
                }

                if ((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.INSTITUTION_AND_APPLICATION_GUID)
                    && auditItem?.objectId) {
                    [institutionGuid, appGuid] = auditItem.objectId.split(AUDIT_USER_ACTION_OBJECT_ID_DELIMITER);
                    auditItem.objectId = appGuid;

                    institutionOrg = this.auditUserActivityService.objectMaps[AuditUserActivityActionObjectIdList.ORGANIZATION_GUID][institutionGuid];
                    application = this.auditUserActivityService.allVendorsApplications.find((app: ApplicationGalleryModel) =>
                        auditItem.objectId === app.guid
                        || auditItem.objectId === app.integrationId
                        || auditItem.objectId === app.dataSourceRef
                    );

                    appName =
                        (institutionOrg?.orgName && application?.name)
                            ? `${institutionOrg?.orgName} - ${application?.name}`
                            : auditItem?.objectId || '';

                    appLink = this.getDistrictAppAdminLinkAuditViewLink(appName, appGuid, institutionGuid, true);

                    if (!appLink) {
                        objectIdOtherText = this.getObjectIdOtherTextAuditViewItem('Application', auditItem?.objectId);
                    }
                }


                if (((AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.APPLICATION_GUID)
                        || (AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.INTEGRATION_ID)
                        || (AuditUserActivityActionObjectIdMap[auditItem.actionName] === AuditUserActivityActionObjectIdList.DNAME))
                    && auditItem?.objectId) {

                    integrationReportItems = this.auditUserActivityService.integrationReportItems;
                    draftIntegrations = this.auditUserActivityService.draftIntegrations;

                    const currentIntegrationReportItem =  integrationReportItems?.find((integrationReportItem: IntegrationReportItem) =>
                        (integrationReportItem.applicationGuid === auditItem?.objectId)
                        || (integrationReportItem.integrationId === auditItem?.objectId)
                        || (integrationReportItem.notEduToneDname === auditItem?.objectId)
                    );

                    if (currentIntegrationReportItem) {
                        appGuid = currentIntegrationReportItem?.applicationGuid;
                        institutionGuid = currentIntegrationReportItem?.organizationGuid;
                        institutionOrg = this.auditUserActivityService.objectMaps[AuditUserActivityActionObjectIdList.ORGANIZATION_GUID][institutionGuid];

                        application = this.auditUserActivityService.allVendorsApplications.find((app: ApplicationGalleryModel) =>
                            appGuid === app.guid
                            || auditItem.objectId === app.integrationId
                            || auditItem.objectId === app.dataSourceRef
                        );

                        appName =
                            (institutionOrg?.orgName && application?.name)
                                ? `${institutionOrg?.orgName} - ${application?.name}`
                                : auditItem?.objectId || '';
                    }


                    const currentDraftIntegration = draftIntegrations?.find((draftIntegration: DraftIntegrationReportItem) =>
                        (draftIntegration.appGuid === auditItem?.objectId)
                        || (draftIntegration.integrationId === auditItem?.objectId)
                        || (draftIntegration.notEduToneDname === auditItem?.objectId)
                    );

                    if (currentDraftIntegration) {
                        appGuid = currentDraftIntegration?.appGuid;
                        institutionGuid = currentDraftIntegration?.vendorOrgGuid;
                        application = this.auditUserActivityService.allVendorsApplications.find((app: ApplicationGalleryModel) =>
                            auditItem.objectId === app.guid
                            || auditItem.objectId === app.integrationId
                            || auditItem.objectId === app.dataSourceRef
                        );
                        institutionOrg = this.auditUserActivityService.objectMaps[AuditUserActivityActionObjectIdList.ORGANIZATION_GUID][institutionGuid];
                        appName = application?.name;
                    }

                    const relatedPage = AuditUserActivityActionRelatedPagesMap[auditItem.actionName];
                    if (relatedPage === AuditUserActivityPagesList.DISTRICT_APP_ADMIN_PUBLIC) {
                        appLink = this.getDistrictAppAdminLinkAuditViewLink(appName, appGuid, institutionGuid, true);
                    } else {
                        appLink = this.getEditAppLinkAuditViewLink(appName, appGuid, institutionGuid, true, !!currentDraftIntegration);
                    }

                    if (!appLink) {
                        objectIdOtherText = this.getObjectIdOtherTextAuditViewItem('Application', auditItem?.objectId);
                    }
                }

                auditItem.view = new AuditUserActivityView(
                    orgLink,
                    null,
                    userObjectIdLink,
                    appLink,
                    objectIdOtherText
                );

                break;

        }

        if (auditItem?.view?.userInitiatorLink) {
            // auditItem.view.userInitiatorLink = this.getEditUserInitiatorAuditViewLink(auditItem, true);
        }

        // if (!auditItem?.view?.orgLink) {
        //     auditItem.view.orgLink = this.getEditOrgAuditViewLink(auditItem, false);
        // }
        // if (!auditItem?.view?.userInitiatorLink) {
        //     auditItem.view.userInitiatorLink = this.getEditUserInitiatorAuditViewLink(auditItem, true);
        // }
    }

    private getObjectIdOtherTextAuditViewItem(title: string, value: string): AuditUserActivityViewLink {
        return new AuditUserActivityViewLink(
            title,
            null,
            null,
            true,
            value
        );
    }

    private getEditOrgAuditViewLink(auditItem: AuditUserActivity, showInListView: boolean): AuditUserActivityViewLink {
        const orgsMap = this.auditUserActivityService.objectMaps[AuditUserActivityActionObjectIdList.ORGANIZATION_GUID];
        const org: DirectoryOrganization | DistrictProfileModel | SchoolProfileModel = orgsMap[auditItem.orgGuid];
        const orgName = (<DirectoryOrganization>org)?.orgName
            || (<DistrictProfileModel>org)?.districtName
            || (<SchoolProfileModel>org)?.schoolName
            || auditItem.orgGuid;

        return new AuditUserActivityViewLink(
            orgName,
            this.getEditOrganizationLink(auditItem.orgGuid),
            AuditUserActivityPagesList.ADMINISTRATION_EDIT_ORG,
            showInListView
        );
    }

    private getEditUserInitiatorAuditViewLink(auditItem: AuditUserActivity, showInListView: boolean): AuditUserActivityViewLink {
        const orgGuid = (auditItem.userEmail === this.SYS_ADMIN_USERNAME) ? OrgsService.GG4L_ORG_ID : auditItem.orgGuid;

        return new AuditUserActivityViewLink(
            auditItem.userEmail,
            this.getEditUserAdminLink(auditItem.userEmail, orgGuid),
            AuditUserActivityPagesList.ADMINISTRATION_EDIT_SOME_ADMIN,
            showInListView
        );
    }

    private getEditUserObjectIdAuditViewLink(auditItem: AuditUserActivity, showInListView: boolean, email?: string): AuditUserActivityViewLink {
        const usersMap = this.auditUserActivityService.objectMaps[AuditUserActivityActionObjectIdList.USER_GUID];
        const user = usersMap[auditItem?.objectId];
        const userEmail: string = email || (<DirectoryUser>usersMap[auditItem.objectId])?.email || auditItem?.objectId || '';

        const userRelatedPage = user
            ? AuditUserActivityService.getEditAdminAuditUserActivityPage(user.role)
            : AuditUserActivityPagesList.ADMINISTRATION_EDIT_SOME_ADMIN;

        const orgGuid = (auditItem.objectId === this.SYS_ADMIN_USERNAME) ? '' : auditItem.orgGuid;

        return new AuditUserActivityViewLink(
            userEmail,
            this.getEditUserAdminLink(auditItem.objectId, orgGuid),
            userRelatedPage,
            showInListView
        );
    }

    private getEditAppLinkAuditViewLink(
        appName: string,
        appGuid: string,
        institutionGuid: string,
        showInListView: boolean,
        isVendorDefaultApp: boolean = false
    ): AuditUserActivityViewLink {
        const routerLink =
                isVendorDefaultApp
                    ? this.getEditDefaultVendorApplicationLink(appGuid, institutionGuid)
                    : this.getEditApplicationLink(appGuid, institutionGuid);
        return new AuditUserActivityViewLink(
            appName,
            routerLink,
            AuditUserActivityPagesList.APPLICATION_SETTINGS,
            showInListView
        );
    }
    private getDistrictAppAdminLinkAuditViewLink(
        appName: string,
        appGuid: string,
        institutionGuid: string,
        showInListView: boolean
    ): AuditUserActivityViewLink {
        return new AuditUserActivityViewLink(
            appName,
            this.getDistrictAppAdminLink(appGuid, institutionGuid),
            AuditUserActivityPagesList.DISTRICT_APP_ADMIN_PUBLIC,
            showInListView
        );
    }

    private getProviderApplication(objectId: string) {
        const application = this.auditUserActivityService.providerApplications
            .find((app: ApplicationProviderListModel) =>
                (app?.integrationId === objectId)
                || (app.guid === objectId)
                || (app.dataSourceRef === objectId)
            );
        return application;
    }

    private getVendorInstitutionsReportItems(objectId: string): VendorInstitutionsReportItem[] {
        return (this.auditUserActivityService.vendorInstitutionsList || [])
            .filter((institution: VendorInstitutionsReportItem) =>
                (institution?.integrationId === objectId)
                || (institution.appGuid === objectId)
                || (institution.notEduToneDname === objectId)
                || (institution.institutionGuid === objectId)
            );
    }

    private getEditApplicationLink(appGuid: string, institutionGuid: string): string {
        return (appGuid && institutionGuid)
            ? `/admin/applications/edit/${appGuid}/${institutionGuid}`
            : '';
    }

    private getEditDefaultVendorApplicationLink(appGuid: string, institutionGuid: string): string {
        return (appGuid && institutionGuid)
            ? `/admin/applications/edit-default/${appGuid}/${institutionGuid}`
            : '';
    }

    private getDistrictAppAdminLink(appGuid: string, institutionGuid: string): string {
        return (appGuid && institutionGuid)
            ? `/admin/a/${appGuid}/org/${institutionGuid}`
            : '';
    }

    private getEditUserAdminLink(userEmailOrUserGuid: string, orgGuid: string) {
        return userEmailOrUserGuid
            ? `/administrations/edit/${orgGuid}${orgGuid ? '/' : ''}${userEmailOrUserGuid}`
            : '';
    }

    private getEditOrganizationLink(orgGuid: string) {
        return (orgGuid)
            ? `/administrations/edit/${orgGuid}`
            : '';
    }

    onOpenUserActivityDetails(row: AuditUserActivity) {
        if (!this.barClosed && !this.barClosing) {
            this.currentUserActivityItem = row;
            this.getUserActionDetails();
        }
    }

    onCloseUserActivityDetails(): void {
        this.currentUserActivityItem = null;
        this.currentUserActivityDetails = null;
    }

    private getUserActionDetails() {
        this.isLoadingDetails = true;
        setTimeout(() => {
            if (this.isLoadingDetails) {
                this.isLoading = true;
            }
        }, 400);

        this.auditUserActivityService.getUserActionDetails(this.currentUserActivityItem.userActionGuid)
            .pipe(takeUntil(this.unsubscribeOnClosePanel$))
            .subscribe((userActionDetails: AuditUserActivity) => {
                    this.setAdditionalData(userActionDetails);
                    this.parseUserActionDetailsParameters(userActionDetails);
                    this.currentUserActivityDetails = userActionDetails;

                    this.isLoadingDetails = false;
                    this.isLoading = false;

                    if (!userActionDetails) {
                        this.snackBar.open(this.LOADING_ERROR_MESSAGE, '', <MatSnackBarConfig>{duration: 3000});
                    }
                },
                (error: any) => {
                    this.snackBar.open(this.LOADING_ERROR_MESSAGE, '', <MatSnackBarConfig>{duration: 3000});
                    this.isLoadingDetails = false;
                    this.isLoading = false;
                    this.currentUserActivityItem = null;
                    this.currentUserActivityDetails = null;
                }
            );
    }

    private parseUserActionDetailsParameters(userActionDetails: AuditUserActivity) {
        if (userActionDetails.parameters) {
            const parametersList = userActionDetails.parameters?.split(',');
            if (parametersList?.length > 0) {
                userActionDetails.parametersList = parametersList.map((param: string) => {
                    const [name, value] = this.splitByFirst(param, '=');
                    return new AuditUserActivityParameter(name, value);
                });
            }
        }
    }

    private splitByFirst(str: string, delimiter: string) {
        const index = str.indexOf(delimiter);
        if (index === -1) {
            return [str, ''];
        }
        return [str.slice(0, index), str.slice(index + delimiter.length)];
    }

    filterUserActions(value: string[]) {
        this.isLoading = true;
        this.hasMoreAuditItems = null;
        this.auditUserActivityData = [];
        this.userActionsLength = null;

        const allCheckboxWasPrevioslyChecked = this.filteredUserActionsList.some((userAction: string) => userAction == this.ALL);
        const allCheckboxIsNowChecked = value.some((userAction: string) => userAction == this.ALL);
        const selectAllCheckboxWasToggled = allCheckboxWasPrevioslyChecked !== allCheckboxIsNowChecked;

        if (selectAllCheckboxWasToggled) {
            if (!allCheckboxWasPrevioslyChecked && allCheckboxIsNowChecked) {
                this.userActionsFilterCtrl.setValue(this.userActionsList);
            }
            if (allCheckboxWasPrevioslyChecked && !allCheckboxIsNowChecked) {
                this.userActionsFilterCtrl.setValue([]);
            }
        } else {
            if (value.length === (this.userActionsList.length - 1)) {
                this.userActionsFilterCtrl.setValue(this.userActionsList);
            }
            if (this.filteredUserActionsList.length === this.userActionsList.length) {
                this.userActionsFilterCtrl.setValue(value.filter((userAction: string) => userAction != this.ALL));
            }
        }

        this.filteredUserActionsList = this.userActionsFilterCtrl.value;
        this.getAuditUserActivityData();
    }

    onBlurPanel() {
        if (!this.barClosed) {
            this.closeBar();
        }
    }

    onNavigate(page: AuditUserActivityPagesList) {
        this.closeBar();
        this.auditUserActivityService.auditUserActivityPageToBeOpenedWhenReady = page;
    }

    private onUnsubscribe(unsubscribe$: Subject<void>) {
        unsubscribe$.next();
        unsubscribe$.complete();
    }

    ngOnDestroy(): void {
        this.onUnsubscribe(this.unsubscribeOnDestroy$);
        this.onUnsubscribe(this.unsubscribeOnClosePanel$);
    }
}
